<div class="container-fluid">
  <div routerLink="/proctoring-dashboard/proto-configs">
    <i class="bi bi-arrow-left back-icon"></i> Back
  </div>

    <ng-container *ngIf="isLoading; else showPage">
    <div class="app-spinner">
      <mat-spinner diameter="50"></mat-spinner>
    </div>
  </ng-container>

  <ng-template #showPage>
    <ng-container *ngIf="configData; else showMessage">
  <h1>New proctoring configuration</h1>

  <p class="subtitle">Default configuration for the organization.</p>

  <form [formGroup]="configEditForm">
    <div class="card">
      <h2>Configuration details</h2>

      <p>Give this configuration a name and describe what it is used for.</p>

      <div class="form-row">
        <div class="field">
          <label> Configuration name <span>*</span></label>

          <input
            placeholder="e.g. High-stakes examination"
            formControlName="name"
          />
          <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['required']">
                  Configuration name is required.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['minLength']">
                  Configuration name must contain at least 3 characters.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['maxLength']">
                  Configuration name cannot exceed 100 characters.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['trimSpaces']">
                  Leading and trailing spaces are not allowed.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['whitespace']">
                  Configuration name cannot be empty.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['invalidCharacters']">
                  Invalid characters are not allowed.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['firstCharacter']">
                  Configuration name cannot start with a special character.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['duplicate']">
                  Configuration name already exists.
                </small>

                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('name')?.touched && configEditForm.get('name')?.errors?.['html']">
                  Invalid input detected.
                </small>
        </div>

        <div class="field">
          <label> Description </label>

          <textarea
            formControlName="description"
            placeholder="Short description of when to use this configuration"
          >
          </textarea>
             <small
            class="smaller-text text-danger"
            *ngIf="configEditForm.get('description')?.touched && configEditForm.get('description')?.errors?.['maxLength']">
            Description cannot exceed 500 characters.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configEditForm.get('description')?.touched && configEditForm.get('description')?.errors?.['trimSpaces']">
            Extra spaces will be removed.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configEditForm.get('description')?.touched && configEditForm.get('description')?.errors?.['whitespace']">
            Please enter a valid description.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configEditForm.get('description')?.touched && configEditForm.get('description')?.errors?.['invalidCharacters']">
            Invalid characters entered.
          </small>

          <small
            class="smaller-text text-danger"
            *ngIf="configEditForm.get('description')?.touched && configEditForm.get('description')?.errors?.['html']">
            Invalid input detected.
          </small>
        </div>
      </div>

      <div class="default-box">
        <div>
          <div class="default-title">
            <b>Set as default</b>

            <span class="default-badge" *ngIf="configData.isDefault"> Default </span>
          </div>

          <p>
            Use this configuration as the organization default for any
            assessment that doesn't specify one.
          </p>
        </div>

        <div
          class="switch"
          [class.active]="configEditForm.get('isDefault')?.value"
          (click)="openDefaultsDialogBox()"
          [class.disabled]="!isEditing"
        >
          <span></span>
        </div>
      </div> 
    </div>

    <div class="card">
      <h2>Browser & device restrictions</h2>

      <p>Control which browsers and devices candidates may use.</p>

      <div class="browser-device">
        <div>
          <label>Allowed browsers<span>*</span></label>

          <div class="chips">
            <button
              type="button"
              *ngFor="let browser of browsers"
              (click)="toggleBrowser(browser)"
              [class.active]="isBrowserSelected(browser)"
            >
              <i class="bi" [class.bi-check]="isBrowserSelected(browser)"> </i>

              {{ browser }}
            </button>
          </div>
          <small
            class="smaller-text text-danger"
            *ngIf="configEditForm.get('allowedBrowsers')?.touched && configEditForm.get('allowedBrowsers')?.hasError('required')
          ">
            Please select at least one browser.
           </small>
        </div>

        <div>
          <label>Allowed devices<span>*</span></label>

          <div class="chips">
            <button
              type="button"
              *ngFor="let device of devices"
              (click)="toggleDevice(device)"
              [class.active]="isDeviceSelected(device)"
            >
              <i class="bi" [class.bi-check]="isDeviceSelected(device)"> </i>
              {{ device }}
            </button>
          </div>
             <small
            class="smaller-text text-danger"
            *ngIf="
              configEditForm.get('allowedDevices')?.touched &&
              configEditForm.get('allowedDevices')?.hasError('required')
            ">
            Please select at least one device.
        </small>
        </div>
      </div>
    </div>

    <div class="card">
      <h2>Capture requirements</h2>

      <p>Choose what candidates must share, and what is recorded for review.</p>

      <div class="capture-grid">
        <div class="capture-box">
          <h3><i class="bi bi-camera-fill"></i> Camera</h3>

          <div class="toggle-row">
            <div class="label-with-info">
              <span> Require camera </span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Candidate must grant camera permission before starting the
                  assessment.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.active]="configEditForm.get('requireCamera')?.value"
              (click)="toggleSwitch('requireCamera')"
            >
              <span></span>
            </div>
          </div>

          <div class="child-toggle">
            <div class="label-with-info">
              <span> Record camera</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Saves camera footage for evidence review.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.disabled]="!configEditForm.get('requireCamera')?.value"
              [class.active]="configEditForm.get('recordCamera')?.value"
              (click)="toggleChildToggle('requireCamera', 'recordCamera')"
            >
              <span></span>
            </div>
          </div>
        </div>

        <div class="capture-box">
          <h3><i class="bi bi-mic-fill"></i> Microphone</h3>

          <div class="toggle-row">
            <div class="label-with-info">
              <span> Require microphone</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Enables detection of background audio and voices.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.active]="configEditForm.get('requireMicrophone')?.value"
              (click)="toggleSwitch('requireMicrophone')"
            >
              <span></span>
            </div>
          </div>

          <div class="child-toggle">
            <div class="label-with-info">
              <span> Record audio </span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Records candidate audio for later review.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.disabled]="!configEditForm.get('requireMicrophone')?.value"
              [class.active]="configEditForm.get('recordAudio')?.value"
              (click)="toggleChildToggle('requireMicrophone', 'recordAudio')"
            >
              <span></span>
            </div>
          </div>
        </div>

        <div class="capture-box">
          <h3><i class="bi bi-display-fill"></i> Screen</h3>

          <div class="toggle-row">
            <div class="label-with-info">
              <span>Require screen sharing</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Candidates must share their entire screen during the
                  assessment.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.active]="configEditForm.get('requireScreenShare')?.value"
              (click)="toggleSwitch('requireScreenShare')"
            >
              <span></span>
            </div>
          </div>

          <div class="child-toggle">
            <div class="label-with-info">
              <span>Record screen</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Stores screen recording for later review.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.disabled]="!configEditForm.get('requireScreenShare')?.value"
              [class.active]="configEditForm.get('recordScreen')?.value"
              (click)="toggleChildToggle('requireScreenShare', 'recordScreen')"
            >
              <span></span>
            </div>
          </div>

          <!-- <div class="toggle-row child-toggle">
            <div class="label-with-info">
              <span>Screen monitoring</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Monitor tab switches, window changes, and full-screen exits.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.disabled]="!configEditForm.get('requireScreenShare')?.value"
              [class.active]="configEditForm.get('screenMonitoring')?.value"
              (click)="
                toggleChildToggle('requireScreenShare', 'screenMonitoring')
              "
            >
              <span></span>
            </div>
          </div>

          <div class="toggle-row child-toggle">
            <div class="label-with-info">
              <span>Screenshot capture</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Periodic screenshots at moments of violation.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.disabled]="!configEditForm.get('requireScreenShare')?.value"
              [class.active]="configEditForm.get('screenshotCapture')?.value"
              (click)="
                toggleChildToggle('requireScreenShare', 'screenshotCapture')
              "
            >
              <span></span>
            </div>
          </div> -->

          <div class="toggle-row">
            <div class="label-with-info">
              <span>Require fullscreen</span>
              <div class="info-tooltip">
                <i class="bi bi-info-circle"></i>
                <div class="tooltip-box">
                  Prevents candidates from switching to other applications.
                </div>
              </div>
            </div>

            <div
              class="toggle"
              [class.active]="configEditForm.get('requireFullscreen')?.value"
              (click)="toggleSwitch('requireFullscreen')"
            >
              <span></span>
            </div>
          </div>
        </div>
      </div>

      <div class="card">
        <h2>Global escalation thresholds</h2>

        <p>
          Configure the maximum number of violations allowed for each severity
          before the configured escalation action is triggered. These thresholds
          apply across the entire assessment session.
        </p>

        <div class="threshold-grid">
          <div class="threshold-box medium">
            <h3>
              <span class="dot medium-dot"></span>
              Medium
            </h3>

            <label>Maximum violations<span>*</span></label>

            <input type="number" min="0" formControlName="maxMedium" />
              <small
            class="text-danger smaller-text"
            *ngIf="configEditForm.get('maxMedium')?.touched && configEditForm.get('maxMedium')?.errors?.['numbersOnly']">
            Only numbers are allowed.
          </small>

          <small
            class="text-danger smaller-text"
            *ngIf="configEditForm.get('maxMedium')?.touched && configEditForm.get('maxMedium')?.errors?.['min']">
            Value cannot be less than 0.
          </small>

          <small
            class="text-danger smaller-text"
            *ngIf="configEditForm.get('maxMedium')?.touched && configEditForm.get('maxMedium')?.errors?.['max']">
            Value cannot be greater than 100.
          </small>

          <small
          class="text-danger smaller-text"
          *ngIf="configEditForm.get('maxMedium')?.errors?.['zero']">
            Value cannot be 0.
          </small>

          <small
               class="text-danger smaller-text"
               *ngIf="configEditForm.get('maxMedium')?.touched && configEditForm.get('maxMedium')?.errors?.['required']">
                Maximum violations is required.
               </small>

            <small>Minor suspicious activities</small>
          </div>

          <div class="threshold-box high">
            <h3>
              <span class="dot high-dot"></span>
              High
            </h3>

            <label>Maximum violations<span>*</span></label>

            <input type="number" min="0" formControlName="maxHigh" />
             <small
                class="text-danger smaller-text"
                *ngIf="configEditForm.get('maxHigh')?.touched && configEditForm.get('maxHigh')?.errors?.['numbersOnly']">
                Only numbers are allowed.
              </small>

              <small
                class="text-danger smaller-text"
                *ngIf="configEditForm.get('maxHigh')?.touched && configEditForm.get('maxHigh')?.errors?.['min']">
                Value cannot be less than 0.
              </small>

              <small
                class="text-danger smaller-text"
                *ngIf="configEditForm.get('maxHigh')?.touched && configEditForm.get('maxHigh')?.errors?.['max']">
                Value cannot be greater than 100.
              </small>
                <small
                  class="text-danger smaller-text"
                  *ngIf="configEditForm.get('maxHigh')?.errors?.['zero']">
              Value cannot be 0.
            </small>
                      <small
            class="text-danger smaller-text"
            *ngIf="configEditForm.get('maxHigh')?.touched && configEditForm.get('maxHigh')?.errors?.['required']">
            Maximum violations is required.
          </small>
            <small>Serious suspicious activities</small>
          </div>

          <div class="threshold-box critical">
            <h3>
              <span class="dot critical-dot"></span>
              Critical
            </h3>

            <label>Maximum violations<span>*</span></label>

            <input type="number" min="0" formControlName="maxCritical" />
             <small
              class="text-danger smaller-text"
              *ngIf="configEditForm.get('maxCritical')?.touched && configEditForm.get('maxCritical')?.errors?.['numbersOnly']">
              Only numbers are allowed.
            </small>

            <small
              class="text-danger smaller-text"
              *ngIf="configEditForm.get('maxCritical')?.touched && configEditForm.get('maxCritical')?.errors?.['min']">
              Value cannot be less than 0.
            </small>

            <small
              class="text-danger smaller-text"
              *ngIf="configEditForm.get('maxCritical')?.touched && configEditForm.get('maxCritical')?.errors?.['max']">
              Value cannot be greater than 100.
            </small>

            <small
              class="text-danger smaller-text"
             *ngIf="configEditForm.get('maxCritical')?.errors?.['zero']">
            Value cannot be 0.
          </small>

                    <small
            class="text-danger smaller-text"
            *ngIf="configEditForm.get('maxCritical')?.touched && configEditForm.get('maxCritical')?.errors?.['required']">
            Maximum violations is required.
          </small>   
            <small>Severe violations</small>
          </div>
        </div>
      </div>

      <div class="footer-buttons">
        <ng-container *hasPermission="PermissionForStudents.PROCTORING_UPDATE">
        <button
          type="button"
          class="save"
          *ngIf="!isEditing"
          (click)="toggleEdit()">
          Edit
        </button>
        </ng-container>
    <ng-container *ngIf="isEditing">
        <button
        type="button"
        class="cancel"
        (click)="cancelEdit()">
        Cancel
      </button>

      <ng-container *hasPermission="PermissionForStudents.PROCTORING_UPDATE">
      <button
        type="button"
        class="save"
        (click)="updateProctoringConfigById()"
        [disabled]="configEditForm.invalid || isSaving">
        Save
      </button>
      </ng-container>
    </ng-container>
      </div>
    </div>
  </form>
  </ng-container>
  <ng-template #showMessage>no configurations details available</ng-template>
  </ng-template>
</div>
